// What Zenith can say about an access point's root password, short of what the
// device runs (#1403; for a generated access point, #1452).
//
// Two readings, each with its own label, and never merged into one word: what
// the cell HOLDS, as a class, and what Zenith's own console attempt MET at the
// device's prompt, as a verdict. A device the cell holds on another password
// and that refused Zenith's answer is the shape #1403 measured, and the screen
// shows the two side by side rather than resolving them.
//
// Nothing here carries a value. The read answers words, and this file renders
// words; there is no password on this page to leak.
import { useEffect, useState } from "react";
import { api } from "../../api/client";
import { SKIP, useQuery } from "../../api/hooks";
import type { components } from "../../api/schema";

type Reading = components["schemas"]["DeviceRootPassword"];
type Verdict = components["schemas"]["ConsoleLoginVerdict"];

/** How often, and for how long, the console window asks again for its own
 * session's verdict. The login phase is bounded at ten seconds and its settle
 * window at one and a half, so a verdict exists well inside the last ask; this
 * is a UI wait, not a measurement of anything. */
export const VERDICT_POLL_MS = 2000;
export const VERDICT_POLL_TRIES = 10;

/** The held class in the operator's words. */
export function heldSentence(held: NonNullable<Reading["held"]>): string {
  // Restates ADR-0257 decision 1d, ADR-0078 — a class is what the cell's copy holds, never what the device runs, and `other` is not a clean bill.
  switch (held) {
    case "absent": return "The controller’s copy of its configuration names no root password, so the device keeps whatever root password it already had.";
    case "empty": return "The controller’s copy of its configuration gives it an empty root password.";
    // The default is the controller vendor's, and the screen names no third party's component
    // (#1678, note `4000995542`): it reads "a publicly known default".
    case "default": return "The controller’s copy of its configuration gives it a publicly known default root password.";
    case "other": return "The controller’s copy of its configuration names a root password that is not the publicly known default. That does not show the device runs it.";
    case "no_document": return "The controller holds no configuration for this device, so there is no root password to read.";
    case "unread": return "The controller could not be read just now.";
  }
  return "The controller answered in a way this page does not recognize.";
}

/** The console verdict in the operator's words, without its date. */
export function verdictSentence(v: Verdict["verdict"]): string {
  // Restates ADR-0257 decision 2c, ADR-0295 — a verdict about Zenith's own attempt, from a fixed vocabulary; what the operator typed afterwards is never recorded.
  switch (v) {
    case "accepted": return "The password on record here for it was accepted at its login prompt.";
    case "refused": return "The password on record here for it was refused at its login prompt.";
    case "no-prompt": return "No login prompt arrived. The device may open a shell with no password, or it was slow to answer.";
    case "closed": return "The session closed before the device answered.";
    case "error": return "The automatic login attempt failed before the device answered.";
    case "not-engaged": return "No single password is on record here for it, so nothing was typed.";
  }
  return "An outcome was recorded that this page does not recognize.";
}

function when(at: string): string {
  const d = new Date(at);
  return Number.isNaN(d.getTime()) ? at : d.toLocaleString();
}

/** The two readings for one carried access point, and the verdict alone for a
 * generated one (ADR-0335: "keep and tell" for the verdict, "not now" for the
 * held class, which the read then leaves out).
 *
 * `sessionId` is the console session this window opened, when there is one:
 * the read is then asked again until a verdict for that session arrives, and a
 * refusal for it is said first and plainly, because the operator is looking at
 * the device's own prompt and the reason is the thing they need (ADR-0257
 * decision 2c). */
export default function RootPasswordReadings({ serial, sessionId }: { serial: string; sessionId?: string }) {
  const [tries, setTries] = useState(0);
  const q = useQuery(() => (serial ? api.GET("/devices/{serial}/root-password", { params: { path: { serial } } }) : SKIP), [serial, tries]);
  const reading = q.data;
  const ours = !!sessionId && reading?.console?.session_id === sessionId;
  const waiting = !!sessionId && !ours && tries < VERDICT_POLL_TRIES;
  useEffect(() => {
    if (!waiting || q.status === "loading") return;
    const t = setTimeout(() => setTries((n) => n + 1), VERDICT_POLL_MS);
    return () => clearTimeout(t);
  }, [waiting, q.status]);

  if (!reading) {
    // An error is not the operator's problem here: this panel is beside a
    // console, and the console is what they came for. It says it could not
    // tell rather than drawing a Problem over the terminal.
    return q.status === "error" ? <p className="muted">What is known here about this device’s root password could not be read.</p> : null;
  }
  const v = reading.console;
  return (
    <div>
      {ours && v?.verdict === "refused" && (
        // Restates ADR-0257 decision 2c and ADR-0335 decision 1 — an access point's refused answer, carried or generated, is told; one attempt was spent and the device's own prompt is what remains.
        <p className="notice" role="status">The password typed at this device’s login prompt on your behalf was refused. You are at the device’s own prompt.</p>
      )}
      <dl className="facts">
        {/* Restates ADR-0257 decision 1d — two readings, each labelled, never merged. ADR-0335
            decision 2 ("not now"): a generated access point's held class is not read, so the
            read leaves `held` out and this says nothing in its place rather than a class. */}
        {reading.held && (
          <>
            <dt>What the controller holds</dt>
            <dd>{heldSentence(reading.held)}</dd>
          </>
        )}
        <dt>Automatic login attempt</dt>
        <dd>
          {v
            ? <>{verdictSentence(v.verdict)} <span className="muted">({when(v.at)})</span></>
            : "No console has been opened on this device since recording began, so nothing has been observed."}
        </dd>
      </dl>
      {/* Restates ADR-0078, ADR-0257 decision 1d, ADR-0335 — no reading here is what the device runs. */}
      <p className="muted">{reading.held ? "Neither of these is what the device itself runs." : "This is not what the device itself runs."}</p>
    </div>
  );
}
